export const ARTIFACT_PANEL_WIDTH_STORAGE_KEY = 'cara.artifactPanelWidth';

/** 56rem, clamped like any saved width on a row too narrow to also hold the conversation's minimum. */
export const DEFAULT_ARTIFACT_PANEL_WIDTH = 896;

/** Narrow enough to read beside a wide conversation, wide enough for the viewer's header actions. */
export const MIN_ARTIFACT_PANEL_WIDTH = 384;

/** The conversation's floor: the panel gives way before the conversation goes below it. */
export const MIN_CONVERSATION_WIDTH = 400;

/** The outline rail's width, mirrored here so the panel can tell whether the rail fits beside it. */
export const OUTLINE_RAIL_WIDTH = 288;

export type PanelWidthBounds = { min: number; max: number };

/**
 * The widths the panel may take in a row of `rowWidth`. An unmeasured row (0)
 * imposes no maximum, so the first paint uses the preference rather than
 * collapsing the panel.
 *
 * A row too narrow for both minimums keeps the conversation's: `min` drops to
 * `max` rather than the panel pushing the conversation under its floor.
 */
export const panelWidthBounds = (rowWidth: number): PanelWidthBounds => {
  if (rowWidth <= 0) {
    return { min: MIN_ARTIFACT_PANEL_WIDTH, max: Infinity };
  }
  const max = Math.max(0, rowWidth - MIN_CONVERSATION_WIDTH);
  return { min: Math.min(MIN_ARTIFACT_PANEL_WIDTH, max), max };
};

export const clampPanelWidth = (width: number, bounds: PanelWidthBounds): number =>
  Math.round(Math.min(bounds.max, Math.max(bounds.min, width)));

/** Whether the outline rail can take its width out of the row without pushing the conversation under its floor. */
export const outlineRailFits = (rowWidth: number, panelWidth: number): boolean =>
  rowWidth <= 0 || rowWidth - panelWidth - OUTLINE_RAIL_WIDTH >= MIN_CONVERSATION_WIDTH;

/**
 * The width last chosen on this device, unclamped: a width saved on a wide
 * monitor is kept as the preference and only clamped for display, so it comes
 * back once the window is wide enough again.
 */
export const readPanelWidthPreference = (): number => {
  try {
    const stored = Number(window.localStorage.getItem(ARTIFACT_PANEL_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_ARTIFACT_PANEL_WIDTH;
  } catch {
    // A browser throws on localStorage when the user blocked site data.
    return DEFAULT_ARTIFACT_PANEL_WIDTH;
  }
};

export const writePanelWidthPreference = (width: number): void => {
  try {
    window.localStorage.setItem(ARTIFACT_PANEL_WIDTH_STORAGE_KEY, String(Math.round(width)));
  } catch {
    // The width still applies for this page; it is only forgotten on reload.
  }
};
